import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { DynamicSEOData } from '../lib/seoUtils';

interface DynamicSEOProps {
  seoData: DynamicSEOData;
  ogImage?: string;
}

export default function DynamicSEO({ seoData, ogImage = '/og-image.jpg' }: DynamicSEOProps) {
  const { i18n } = useTranslation();
  const currentLang = i18n.language;
  const siteUrl = 'https://workio.bg';
  const fullCanonicalUrl = `${siteUrl}${seoData.canonicalUrl}`;
  const fullOgImage = ogImage.startsWith('http') ? ogImage : `${siteUrl}${ogImage}`;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{seoData.title}</title>
      <meta name="description" content={seoData.description} />
      <meta name="keywords" content={seoData.keywords} />
      <meta name="language" content={currentLang} />
      <meta name="robots" content={seoData.noIndex ? 'noindex,nofollow' : 'index,follow'} />
      <link rel="canonical" href={fullCanonicalUrl} />
      
      {/* Open Graph Meta Tags */}
      <meta property="og:title" content={seoData.title} />
      <meta property="og:description" content={seoData.description} />
      <meta property="og:image" content={fullOgImage} />
      <meta property="og:url" content={fullCanonicalUrl} />
      <meta property="og:type" content={seoData.ogType} />
      <meta property="og:site_name" content="Workio" />
      <meta property="og:locale" content={currentLang === 'bg' ? 'bg_BG' : 'en_US'} />
      
      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={seoData.title} />
      <meta name="twitter:description" content={seoData.description} />
      <meta name="twitter:image" content={fullOgImage} />
      
      {/* Additional Meta Tags */}
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta name="theme-color" content="#28A0F0" />
      <meta name="author" content="Workio" />
      <meta name="generator" content="React" />
      
      {/* Structured Data */}
      {seoData.structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(seoData.structuredData)}
        </script>
      )}
    </Helmet>
  );
}

